import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spin, Flex } from 'antd';

import ProtectedRoute from './ProtectedRoute';
import AppLayout from '../components/layout/AppLayout';
import LoginPage from '../pages/Login';
import NonAutorise from '../pages/NonAutorise';
import ComingSoon from '../pages/ComingSoon';

// Route-level code splitting — each page loaded lazily when first accessed
// Phases 2-15 will replace these ComingSoon stubs with real implementations

// Phase 2 — lazy imports
const ClientsPage = lazy(() => import('../pages/Clients'));
const ClientDetailPage = lazy(() => import('../pages/Clients/[id]'));
const PersonnelPage = lazy(() => import('../pages/Personnel'));
const ServicesPage = lazy(() => import('../pages/Services'));
const FournisseursPage = lazy(() => import('../pages/Fournisseurs'));

// Phase 3 — lazy imports
const RessourcesPage = lazy(() => import('../pages/Ressources'));
const AdminCalendrierPage = lazy(() => import('../pages/Admin/Calendrier'));

// Phase 4 — lazy imports
const ProjetsPage = lazy(() => import('../pages/Projets'));
const ProjetDetailPage = lazy(() => import('../pages/Projets/[id]'));

// Phase 5 — lazy imports
const ParticipantsPage = lazy(() => import('../pages/Participants'));
const ParticipantDetailPage = lazy(() => import('../pages/Participants/[id]'));

// Phase 7 — lazy imports
const AdminGabaritsPage = lazy(() => import('../pages/Admin/Gabarits'));

// Phase 14 — lazy imports
const AdminUtilisateursPage = lazy(() => import('../pages/Admin/Utilisateurs'));
const AdminPermissionsPage = lazy(() => import('../pages/Admin/Permissions'));
const AdminParametresPage = lazy(() => import('../pages/Admin/Parametres'));
const AdminImportPage = lazy(() => import('../pages/Admin/Import'));
const ComptabilitePage = lazy(() => import('../pages/Admin/Comptabilite'));

// Phase 15b — lazy imports (public portal, no auth)
const PublicOfferPage = lazy(() => import('../pages/Public/Offer'));
const PublicInvoicePage = lazy(() => import('../pages/Public/Invoice'));

// Phase 15 — lazy imports (public portal, no auth)
const PublicHorairePage = lazy(() => import('../pages/Public/Horaire'));
const PublicRegisterPage = lazy(() => import('../pages/Public/Register'));

// Phase 8 — lazy imports
const FinanceOffresPage = lazy(() => import('../pages/Finance/Offres'));
const FinanceOffreDetailPage = lazy(() => import('../pages/Finance/Offres/[id]'));
const FinanceFacturesPage = lazy(() => import('../pages/Finance/Factures'));
const FinanceSecretariatPage = lazy(() => import('../pages/Finance/Secretariat'));
const FinanceTransfertsPage = lazy(() => import('../pages/Finance/Transferts'));
const LiaisonDofinPage = lazy(() => import('../pages/LiaisonDofin'));

// Phase 9 — lazy imports
const BudgetPage = lazy(() => import('../pages/Budget'));
const BudgetDetailPage = lazy(() => import('../pages/Budget/[id]'));

// Phase 12 — lazy imports
const DashboardPage = lazy(() => import('../pages/Dashboard'));
const RapportsPage = lazy(() => import('../pages/Rapports'));

// Phase 6 — lazy imports
const SchedulePage = lazy(() => import('../pages/Schedule'));

// Phase 10 — lazy imports
const GanttPage = lazy(() => import('../pages/Gantt'));

const PageSpinner = () => (
  <Flex justify="center" align="center" style={{ height: '60vh' }}>
    <Spin size="large" />
  </Flex>
);

const lazy_ = (factory: () => Promise<{ default: React.ComponentType }>) => {
  const Component = lazy(factory);
  return (
    <Suspense fallback={<PageSpinner />}>
      <Component />
    </Suspense>
  );
};

export default function AppRouter(): React.ReactElement {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/connexion" element={<LoginPage />} />
      <Route path="/non-autorise" element={<NonAutorise />} />

      {/* Phase 15 — Public trainer schedule link and public registration (no auth) */}
      <Route
        path="/horaire-public/:token"
        element={
          <Suspense fallback={<PageSpinner />}>
            <PublicHorairePage />
          </Suspense>
        }
      />
      <Route
        path="/inscription/:group_id"
        element={
          <Suspense fallback={<PageSpinner />}>
            <PublicRegisterPage />
          </Suspense>
        }
      />

      {/* Phase 15b — Public client approval portal (no auth) */}
      <Route
        path="/offer/:token"
        element={
          <Suspense fallback={<PageSpinner />}>
            <PublicOfferPage />
          </Suspense>
        }
      />
      <Route
        path="/facture/:token"
        element={
          <Suspense fallback={<PageSpinner />}>
            <PublicInvoicePage />
          </Suspense>
        }
      />

      {/* Authenticated app shell */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        {/* Home — Gantt (Phase 10) */}
        <Route index element={<Suspense fallback={<PageSpinner />}><GanttPage /></Suspense>} />

        {/* Clients (ex-CRM) — Phase 2 */}
        <Route
          path="clients"
          element={
            <ProtectedRoute module="crm" action="read">
              <Suspense fallback={<PageSpinner />}><ClientsPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="clients/:id/*"
          element={
            <ProtectedRoute module="crm" action="read">
              <Suspense fallback={<PageSpinner />}><ClientDetailPage /></Suspense>
            </ProtectedRoute>
          }
        />

        {/* Redirections — URLs que les liens directs doivent encore résoudre */}
        <Route path="dofin" element={<Navigate to="/admin/connexions" replace />} />
        <Route path="opportunites" element={<Navigate to="/clients" replace />} />

        {/* Participants — Phase 5 */}
        <Route
          path="participants"
          element={
            <ProtectedRoute module="participants" action="read">
              <Suspense fallback={<PageSpinner />}><ParticipantsPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="participants/:id"
          element={
            <ProtectedRoute module="participants" action="read">
              <Suspense fallback={<PageSpinner />}><ParticipantDetailPage /></Suspense>
            </ProtectedRoute>
          }
        />

        {/* Projets — Phase 4 */}
        <Route
          path="projets"
          element={
            <ProtectedRoute module="projects" action="read">
              <Suspense fallback={<PageSpinner />}><ProjetsPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="projets/:id/*"
          element={
            <ProtectedRoute module="projects" action="read">
              <Suspense fallback={<PageSpinner />}><ProjetDetailPage /></Suspense>
            </ProtectedRoute>
          }
        />

        {/* Ressources — Phase 3 */}
        <Route
          path="ressources"
          element={
            <ProtectedRoute module="resources" action="read">
              <Suspense fallback={<PageSpinner />}><RessourcesPage /></Suspense>
            </ProtectedRoute>
          }
        />

        {/* Catalogue services & fournisseurs — Phase 2 */}
        <Route
          path="services"
          element={
            <ProtectedRoute module="crm" action="read">
              <Suspense fallback={<PageSpinner />}><ServicesPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="fournisseurs"
          element={
            <ProtectedRoute module="crm" action="read">
              <Suspense fallback={<PageSpinner />}><FournisseursPage /></Suspense>
            </ProtectedRoute>
          }
        />

        {/* Personnel — Phase 2 */}
        <Route
          path="personnel"
          element={
            <ProtectedRoute module="schedule" action="read">
              <Suspense fallback={<PageSpinner />}><PersonnelPage /></Suspense>
            </ProtectedRoute>
          }
        />

        {/* Horaire — Phase 6 */}
        <Route
          path="horaire"
          element={
            <ProtectedRoute module="schedule" action="read">
              <Suspense fallback={<PageSpinner />}><SchedulePage /></Suspense>
            </ProtectedRoute>
          }
        />

        {/* Finance — Phase 8 */}
        <Route
          path="finance"
          element={
            <ProtectedRoute module="finance" action="read">
              <ComingSoon title="Finance — Phase 8" />
            </ProtectedRoute>
          }
        />
        <Route
          path="finance/offres"
          element={
            <ProtectedRoute module="finance" action="read">
              <Suspense fallback={<PageSpinner />}><FinanceOffresPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="finance/offres/:offerId"
          element={
            <ProtectedRoute module="finance" action="read">
              <Suspense fallback={<PageSpinner />}><FinanceOffreDetailPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="finance/factures"
          element={
            <ProtectedRoute module="finance" action="read">
              <Suspense fallback={<PageSpinner />}><FinanceFacturesPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="finance/historique"
          element={
            <ProtectedRoute module="finance" action="read">
              <ComingSoon title="Historique financier — Phase 8" />
            </ProtectedRoute>
          }
        />
        <Route
          path="finance/transferts"
          element={
            <ProtectedRoute module="finance" action="read">
              <Suspense fallback={<PageSpinner />}><FinanceTransfertsPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="finance/liaison-dofin"
          element={
            <ProtectedRoute module="finance" action="write">
              <Suspense fallback={<PageSpinner />}><LiaisonDofinPage /></Suspense>
            </ProtectedRoute>
          }
        />
        {/* Legacy redirects — keep old bookmarks working */}
        <Route
          path="finance/secretariat"
          element={<Navigate to="/finance/liaison-dofin" replace />}
        />

        {/* Budget — Phase 9 */}
        <Route
          path="budget"
          element={
            <ProtectedRoute module="budget" action="read">
              <Suspense fallback={<PageSpinner />}><BudgetPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="budget/:calcId"
          element={
            <ProtectedRoute module="budget" action="read">
              <Suspense fallback={<PageSpinner />}><BudgetDetailPage /></Suspense>
            </ProtectedRoute>
          }
        />

        {/* Tableau de bord — Phase 12 */}
        <Route
          path="tableau-de-bord/*"
          element={
            <ProtectedRoute module="dashboard" action="read">
              <Suspense fallback={<PageSpinner />}>
                <DashboardPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Rapports — Phase 13 */}
        <Route
          path="rapports"
          element={
            <ProtectedRoute module="dashboard" action="read">
              <Suspense fallback={<PageSpinner />}>
                <RapportsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Archives */}
        <Route
          path="archives"
          element={
            <ProtectedRoute module="archive" action="read">
              <ComingSoon title="Archives" />
            </ProtectedRoute>
          }
        />

        {/* Admin — Phase 14 */}
        <Route path="admin">
          <Route
            path="utilisateurs"
            element={
              <ProtectedRoute module="admin" action="manage_users">
                <Suspense fallback={<PageSpinner />}><AdminUtilisateursPage /></Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="permissions"
            element={
              <ProtectedRoute module="admin" action="manage_permissions">
                <Suspense fallback={<PageSpinner />}><AdminPermissionsPage /></Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="parametres"
            element={
              <ProtectedRoute module="admin" action="manage_settings">
                <Suspense fallback={<PageSpinner />}><AdminParametresPage /></Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="gabarits"
            element={
              <ProtectedRoute module="documents" action="manage_templates">
                <Suspense fallback={<PageSpinner />}><AdminGabaritsPage /></Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="gabarits-courriel"
            element={
              <ProtectedRoute module="documents" action="manage_templates">
                {lazy_(() => import('../pages/GabaritsCourriel'))}
              </ProtectedRoute>
            }
          />
          <Route
            path="regles-alertes"
            element={
              <ProtectedRoute module="admin" action="manage_settings">
                {lazy_(() => import('../pages/ReglesAlertes'))}
              </ProtectedRoute>
            }
          />
          <Route
            path="connexions"
            element={<Navigate to="/finance/liaison-dofin" replace />}
          />
          <Route
            path="comptabilite"
            element={
              <ProtectedRoute module="admin" action="manage_settings">
                <Suspense fallback={<PageSpinner />}><ComptabilitePage /></Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="calendrier"
            element={
              <ProtectedRoute module="admin" action="manage_settings">
                <Suspense fallback={<PageSpinner />}><AdminCalendrierPage /></Suspense>
              </ProtectedRoute>
            }
          />
          {/* Types de formation are managed within the Services page — redirect */}
          <Route path="types-formation" element={<Navigate to="/services" replace />} />
          <Route
            path="import"
            element={
              <ProtectedRoute module="admin" action="manage_settings">
                <Suspense fallback={<PageSpinner />}><AdminImportPage /></Suspense>
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
